import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useContext } from 'react';
import AuthContext from '../context/AuthContext';
import PropTypes from 'prop-types';

const ProtectedRoute = ({ allowedRoles }) => {
    const { userrole, loading } = useContext(AuthContext);
    const location = useLocation();

    // Get role from both AuthContext and localStorage (fallback for race conditions)
    const storedRole = localStorage.getItem('userrole');
    const token = localStorage.getItem('token');

    // ULTRA-AGGRESSIVE BYPASS: Check localStorage FIRST before anything else
    // If we have token + role "4" + this is a state admin route, allow immediately
    // ALSO allow if we're trying to access the state dashboard path specifically
    if (token && storedRole === '4') {
        const isStateAdminRoute = allowedRoles && (allowedRoles.includes("4") || allowedRoles.includes(4));
        const isStateDashboardPath = location.pathname === '/admin/state-dashboard' || location.pathname.includes('state-dashboard');

        if (isStateAdminRoute || isStateDashboardPath) {
            return <Outlet />;
        }
    }

    // ADDITIONAL BYPASS: If we're on state dashboard path and have role 4, allow regardless of allowedRoles
    // This catches cases where the route might not be properly configured
    if (location.pathname === '/admin/state-dashboard' && token && storedRole === '4') {
        return <Outlet />;
    }

    const currentRole = userrole || storedRole || null;

    // Normalize roles for comparison - handle both string and number types
    const normalizeRole = (role) => {
        if (role === "admin" || role === "Admin" || role === "ADMIN") return "admin";
        // Multi-school admin has the same in-app surface as a school admin (role 1),
        // just scoped to several schools via the school switcher. Treat as 1 for routing.
        if (role === "multi_school_admin") return 1;
        if (role === null || role === undefined || role === "") return null;
        const numRole = typeof role === 'string' ? parseInt(role, 10) : role;
        return isNaN(numRole) ? role : numRole;
    };

    const normalizedUserRole = normalizeRole(currentRole);
    const normalizedAllowedRoles = allowedRoles.map(role => normalizeRole(role));

    // SPECIAL HANDLING: If user is admin, allow access to admin routes immediately
    if ((currentRole === "admin" || storedRole === "admin" || normalizedUserRole === "admin") &&
        (normalizedAllowedRoles.includes("admin") || allowedRoles.includes("admin"))) {
        return <Outlet />;
    }

    // If we have a role in localStorage but AuthContext is still loading, allow access
    // This handles the race condition where navigation happens before AuthContext updates
    if (loading && storedRole) {
        const normalizedStoredRole = normalizeRole(storedRole);
        // Try multiple comparison methods
        const hasAccess = normalizedAllowedRoles.includes(normalizedStoredRole) ||
            normalizedAllowedRoles.includes(String(normalizedStoredRole)) ||
            normalizedAllowedRoles.includes(Number(normalizedStoredRole)) ||
            normalizedAllowedRoles.some(role => String(role) === String(normalizedStoredRole)) ||
            normalizedAllowedRoles.some(role => Number(role) === Number(normalizedStoredRole));
        if (hasAccess) {
            return <Outlet />;
        }
    }

    // Only show loading if we don't have a role in localStorage
    if (loading && !storedRole) {
        return <div>Loading...</div>;
    }

    // No user role - redirect to login (check both AuthContext and localStorage)
    // BUT: Don't redirect if we have a token (might be a timing issue)
    if (!currentRole && !loading && !token) {
        console.warn("ProtectedRoute: No user role, redirecting to login", {
            userrole,
            storedRole,
            loading,
            pathname: location.pathname,
            hasToken: !!token
        });
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    // If we have a token but no role yet, wait a bit (might be loading)
    if (token && !currentRole && !loading) {
        // Give it a moment for AuthContext to catch up
        return <div>Loading...</div>;
    }

    // Check if user role matches allowed roles (handle both number and string comparisons)
    // Try multiple comparison methods to ensure we catch all cases
    // SPECIAL: Check for "admin" role explicitly first
    const isAdmin = normalizedUserRole === "admin" || currentRole === "admin" || storedRole === "admin";
    const allowsAdmin = normalizedAllowedRoles.includes("admin") || allowedRoles.includes("admin");

    // If user is admin and route allows admin, grant access immediately
    if (isAdmin && allowsAdmin) {
        return <Outlet />;
    }

    const hasAccess = normalizedAllowedRoles.includes(normalizedUserRole) ||
        normalizedAllowedRoles.includes(String(normalizedUserRole)) ||
        normalizedAllowedRoles.includes(Number(normalizedUserRole)) ||
        normalizedAllowedRoles.some(role => String(role) === String(normalizedUserRole)) ||
        normalizedAllowedRoles.some(role => Number(role) === Number(normalizedUserRole)) ||
        // Also check raw values
        allowedRoles.includes(currentRole) ||
        allowedRoles.includes(storedRole) ||
        allowedRoles.includes(String(currentRole)) ||
        allowedRoles.includes(String(storedRole));

    // Access check logging removed for production

    if (!hasAccess) {
        // FINAL CHECK: If we're trying to access state dashboard and have role 4, allow anyway
        // This is a last resort bypass in case all other checks failed
        if (location.pathname === '/admin/state-dashboard' && token && storedRole === '4') {
            return <Outlet />;
        }

        // FINAL CHECK: If we're trying to access admin routes and have admin role, allow anyway
        if ((location.pathname.includes('/admin/') || location.pathname.startsWith('/admin')) &&
            token && (storedRole === 'admin' || currentRole === 'admin')) {
            return <Outlet />;
        }

        // Log the access denial for debugging
        console.warn("ProtectedRoute: Access denied", {
            userRole: userrole,
            storedRole: storedRole,
            currentRole: currentRole,
            normalizedUserRole: normalizedUserRole,
            allowedRoles: allowedRoles,
            normalizedAllowedRoles: normalizedAllowedRoles,
            requestedPath: location.pathname,
            isAdmin: isAdmin,
            allowsAdmin: allowsAdmin,
            comparisonDetails: {
                includes: normalizedAllowedRoles.includes(normalizedUserRole),
                stringIncludes: normalizedAllowedRoles.includes(String(normalizedUserRole)),
                numberIncludes: normalizedAllowedRoles.includes(Number(normalizedUserRole))
            }
        });

        // Redirect to appropriate dashboard based on user role instead of /not-found
        let redirectPath = "/not-found"; // Default fallback

        if (normalizedUserRole === 1) {
            redirectPath = "/main-dashboard";
        } else if (normalizedUserRole === 2) {
            redirectPath = "/schoolteacher-dashboard";
        } else if (normalizedUserRole === 3) {
            redirectPath = "/teacher-dashboard";
        } else if (normalizedUserRole === 4) {
            redirectPath = "/admin/state-dashboard";
        } else if (normalizedUserRole === "admin" || currentRole === "admin" || storedRole === "admin") {
            redirectPath = "/admin/admin-dashboard";
        }

        return <Navigate to={redirectPath} replace />;
    }

    return <Outlet />;
};

ProtectedRoute.propTypes = {
    allowedRoles: PropTypes.arrayOf(PropTypes.string).isRequired, // Fix PropTypes
};

export default ProtectedRoute;